import React, { useState, useEffect } from 'react';
import { Eye, Download, Filter, Search, Calendar, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';

const AccessLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  });

  const [filter, setFilter] = useState({ status: 'all', method: 'all' });
  const [searchTerm, setSearchTerm] = useState('');

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'text-green-400';
    if (status >= 400 && status < 500) return 'text-yellow-400';
    if (status >= 500) return 'text-red-400';
    return 'text-slate-400';
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'text-blue-400';
      case 'POST': return 'text-green-400';
      case 'PUT': return 'text-yellow-400';
      case 'DELETE': return 'text-red-400';
      default: return 'text-slate-400';
    }
  };

  // API functions
  const fetchLogs = async (resetOffset = false) => {
    try {
      const token = localStorage.getItem('token');
      const offset = resetOffset ? 0 : pagination.offset;
      
      const params = new URLSearchParams({
        limit: pagination.limit,
        offset: offset,
        ...(filter.status !== 'all' && { status: filter.status }),
        ...(filter.method !== 'all' && { method: filter.method }),
        ...(searchTerm && { search: searchTerm })
      });

      const response = await fetch(`/api/proxy/logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        if (resetOffset) {
          setLogs(result.data.logs);
          setPagination(prev => ({ ...prev, offset: 0, ...result.data.pagination }));
        } else {
          setLogs(prev => offset === 0 ? result.data.logs : [...prev, ...result.data.logs]);
          setPagination(prev => ({ ...prev, ...result.data.pagination }));
        }
        setError(null);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to fetch access logs');
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        format: 'csv',
        ...(filter.status !== 'all' && { status: filter.status }),
        ...(filter.method !== 'all' && { method: filter.method }),
        ...(searchTerm && { search: searchTerm })
      });

      const response = await fetch(`/api/proxy/logs/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `access-logs-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Logs exported successfully');
      } else {
        toast.error('Failed to export logs');
      }
    } catch (err) {
      toast.error('Failed to export logs');
      console.error('Error exporting logs:', err);
    }
  };

  const refreshLogs = () => {
    setLoading(true);
    setPagination(prev => ({ ...prev, offset: 0 }));
    fetchLogs(true);
  };

  const loadMoreLogs = () => {
    if (pagination.hasMore) {
      setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
      fetchLogs(false);
    }
  };

  useEffect(() => {
    fetchLogs(true);
  }, [filter, searchTerm]);

  useEffect(() => {
    if (pagination.offset > 0) {
      fetchLogs(false);
    }
  }, [pagination.offset]);

  useEffect(() => {
    // Auto-refresh every 30 seconds
    const interval = setInterval(refreshLogs, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2 text-slate-400">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading access logs...</span>
        </div>
      </div>
    );
  }

  if (error && logs.length === 0) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
        <div className="flex items-center space-x-2 text-red-400">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-medium">Error loading access logs</span>
        </div>
        <p className="text-red-300 mt-2">{error}</p>
        <button 
          onClick={refreshLogs}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Eye className="w-6 h-6 text-blue-400 mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-slate-200">Apache Access Logs</h2>
                <p className="text-slate-400 mt-1">Real-time proxy access logs from ksp3plhq.com</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={refreshLogs}
                disabled={loading}
                className="px-3 py-2 bg-slate-700 text-slate-200 rounded-md hover:bg-slate-600 flex items-center disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button 
                onClick={exportLogs}
                className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search logs by IP, path, or user agent..."
                  className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Status Filter */}
            <select
              className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filter.status}
              onChange={(e) => setFilter({...filter, status: e.target.value})}
            >
              <option value="all">All Status</option>
              <option value="2xx">2xx Success</option>
              <option value="4xx">4xx Client Error</option>
              <option value="5xx">5xx Server Error</option>
            </select>

            {/* Method Filter */}
            <select
              className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filter.method}
              onChange={(e) => setFilter({...filter, method: e.target.value})}
            >
              <option value="all">All Methods</option>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  IP Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Path
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Size
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-600">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-slate-700/50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300 font-mono">
                    {log.timestamp}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300 font-mono">
                    {log.ip}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <span className={`${getMethodColor(log.method)} font-mono`}>{log.method}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-300 font-mono max-w-xs truncate">
                    {log.path}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <span className={`${getStatusColor(log.status)} font-mono`}>{log.status}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400 font-mono">
                    {log.size}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-slate-600">
          <div className="flex items-center justify-between text-sm text-slate-400">
            <span>Showing {logs.length} of {pagination.total} entries</span>
            {pagination.hasMore && (
              <button 
                onClick={loadMoreLogs}
                disabled={loading}
                className="px-3 py-1 bg-slate-700 rounded hover:bg-slate-600 disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessLogs;